import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useCallback, useState } from 'react';
import { canvasToImage } from '@/utils/dom';
import {
  FileNameTypes,
  FileUploadStatus,
  PaintBlockPointsDocument,
  useUpdateFileStatusMutation,
  useUploadFilesMutation,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { AnalysisCustomBlockWithFiles } from '@/types';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { DEFAULT_SCREENSHOT_DARK_BACKGROUND } from '@/constants';
import axios from 'axios';
import { generateUUIDv4 } from '@/utils/string';

const base64ToFile = (base64: string, fileName: string): File => {
  const arr = base64.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], fileName, { type: mime });
};

export const useSaveIsolateCustomBlockScreenshot = (
  block: AnalysisCustomBlockWithFiles | undefined
) => {
  const callbacks = useAtomValue(state.globalCallbacks);
  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({});
  const [uploadFile, { error, loading: uploadLoading }] = useUploadFilesMutation();
  const [updateFileStatus, { loading: fileStatusChangeLoading }] = useUpdateFileStatusMutation();

  const [uploadingToCloud, setUploadingToCloud] = useState(false);

  const uploadCuboidScreenshot = useCallback(async () => {
    if (!block || !block?.paintRegionId) {
      showSnackBar('Invalid block. Please contact support', 'error');
      return;
    }

    // Getting the screenshot from the viewer using it's native callbaack
    const htmlCanvas = callbacks?.getHTMLCanvas && callbacks.getHTMLCanvas();

    // Convert the canvas to an image, in base64 format
    const imageBase64 = canvasToImage(htmlCanvas, {
      fillStyle: DEFAULT_SCREENSHOT_DARK_BACKGROUND,
    });

    // This wouldn't be a problem, but checking just in case
    if (!imageBase64) {
      showSnackBar('Error taking screenshot', 'error');
      return;
    }

    // Generating a file name, it does not have to be strictly in this format
    // but this looked good
    // Adding in date, to make the name unique since we have that
    // constraint in the backend
    const uniqueId = generateUUIDv4().slice(0, 8)
    const fileName = `cuboid_screenshot_${block?.name}-${uniqueId}.jpg`;


    // First generate a pre-signed URL to upload the file to S3
    // This does not upload the file YET, but it creates a sort of 'placeholder'
    // for the file in the database
    const response = await uploadFile({
      variables: {
        input: {
          structureId,
          collectionName: 'paintRegion',
          referenceId: block?.paintRegionId,
          fileNameType: FileNameTypes.UserAttachments,
          files: [
            {
              mimeType: 'image/jpeg',
              name: fileName,
              size: imageBase64.length,
              uuid: generateUUIDv4(),
            },
          ],
        },
      },
    });

    // After the backend has successfully created the pre-signed URL, it
    // returns it with a couple of other attributes like file id (auto generated) by the BE
    // The .url property is the URL part, where we will be uploading the file
    const preSignedObject = response?.data?.preSignedUrlToUploadFiles?.[0];

    // Checking just in case. This should not happen
    if (!preSignedObject) {
      showSnackBar('Error uploading screenshot', 'error');
      return;
    }

    // Converting the base64 string to a file object
    // This is needed because uploading to S3 requires a file object
    // and not a base64 string
    const file = base64ToFile(imageBase64, fileName);

    try {
      setUploadingToCloud(true);
      // Actually uploading the file to S3 using the pre-signed URL
      const uploadResponse = await axios.put(preSignedObject.url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });
      if (uploadResponse.status !== 200) {
        throw new Error('Error uploading file');
      }

      // At this stage the file is on S3 and there is an entry for it in the database
      // Now we need to update the file status in the database, to indicate that the file
      // has been successfully uploaded

      const statusChangeResponse = await updateFileStatus({
        variables: {
          input: {
            referenceId: block?.paintRegionId,
            collectionName: 'paintRegion',

            // Using the file id returned from the pre-signed URL response
            fileUpdates: [{ uploadStatus: FileUploadStatus.Success, fileId: preSignedObject.id }],
          },
        },
        refetchQueries: [PaintBlockPointsDocument]
      });

      if (!statusChangeResponse?.data?.updateFilesStatus) {
        throw new Error('Error updating file status');
      }

      showSnackBar('Screenshot uploaded successfully', 'success');
    } catch {
      showSnackBar('Error uploading to the cloud', 'error');
      return;
    } finally {
      setUploadingToCloud(false);
    }
  }, [block, callbacks, showSnackBar, structureId, updateFileStatus, uploadFile]);

  const loading = uploadLoading || uploadingToCloud || fileStatusChangeLoading;

  return { uploadCuboidScreenshot, error, loading };
};
